import React, { useState, useEffect, useRef } from "react";
import { useIntl } from "react-intl";
import ButtonWithHover from "./ButtonWithHover";
import { MatchQuizPlay, FillBlanksQuizPlay, TextAnswerQuizPlay } from "./MatchFillQuizPlay";
import {
	resolveUrl,
	normalizeLetterSpacing,
	normalizeLineHeight,
	getDefaultHoverColor,
	isShapeGradient,
	getTextEffectPresentation,
	getElementEffectPresentation,
	ensureTextEffectStylesInjected,
	buttonElementNeedsEffectOverflow,
	mergeBoxShadow,
	pickEffectBundle,
	hasActiveEffectBundle,
	resolveImageBorderCss,
} from "./erRendererStyleUtils";
import {
	getQuizBlockBackground,
	getQuizBlockBorderCss,
	getQuizBlockBoxShadow,
	getQuizTrueFalseColors,
	getQuizOptionStyles,
	QUIZ_OPTION_DESIGNS,
	resolveQuizOptionBorderPresentation,
	buildTextAnswerFieldPresentation,
} from "./erQuizPlayStyles";
import { LEGACY_LOGO_REGISTRATION_ROLE } from "../../utils/legacyClientRegistration";
import { EscapeRoomTypewriterTextDiv } from "./escapeRoomTypewriterText";
import { pickErViewportFromSize, getQuizOptionsLayoutForBreakpoint } from "./erViewport";
import { resolveTextTypewriterDurationSec } from "./erEntryAnimation";
import { applyEscapeRoomVideoPlaybackRate } from "./escapeRoomVideoPlayback";
import { getImageElementTransformStyle } from "./escapeRoomImageTransform";
import { isScoreTextElement, resolveScoreTextDisplayHtml } from "./escapeRoomScoreText";
import { useMedia } from "../../contexts/MediaContext";

function EscapeRoomAudioElement({ element, style }) {
	const resolved = resolveUrl(element.content?.url);
	const audioRef = useRef(null);
	const { isMediaUnlocked } = useMedia();
	const hidden = !!element.content?.hiddenInPlay;
	const volRaw = Number(element.content?.volume);
	const volume = Number.isFinite(volRaw) ? Math.min(1, Math.max(0, volRaw)) : 0.7;
	const loop = !!element.content?.loop;
	const autoplay = element.content?.autoplay !== false;

	useEffect(() => {
		const node = audioRef.current;
		if (!node || !resolved) return undefined;
		node.volume = volume;
		node.loop = loop;
		if (autoplay && isMediaUnlocked) {
			try {
				const p = node.play();
				if (p && typeof p.catch === "function") p.catch(() => {});
			} catch (_) {}
		}
		return () => {
			try {
				node.pause();
			} catch (_) {}
		};
	}, [resolved, volume, loop, autoplay, isMediaUnlocked, element.content?.url]);

	if (!resolved) {
		return <div style={{ ...style, backgroundColor: "transparent" }} />;
	}

	return (
		<div
			style={{
				...style,
				overflow: "hidden",
				backgroundColor: "transparent",
				...(hidden ? { opacity: 0, pointerEvents: "none" } : {}),
			}}
		>
			<audio
				ref={audioRef}
				src={resolved}
				preload="auto"
				loop={loop}
				autoPlay={autoplay && isMediaUnlocked}
				controls={!hidden}
				data-er-autoplay={autoplay ? "1" : "0"}
				style={{
					width: "100%",
					height: hidden ? 0 : "100%",
					maxHeight: hidden ? 0 : "100%",
				}}
			/>
		</div>
	);
}

function EscapeRoomVideoElement({ element, style, onButtonAction }) {
	const intl = useIntl();
	const resolved = resolveUrl(element.content?.url);
	const playbackRate = element.content?.playbackRate ?? 1;
	const advanceOnEnd = !!element.content?.advanceOnVideoEnd;
	const videoLoop = !!element.content?.loop && !advanceOnEnd;
	const [videoFailed, setVideoFailed] = useState(false);
	const stallTimerRef = useRef(null);

	useEffect(() => {
		setVideoFailed(false);
	}, [element.content?.url]);

	useEffect(() => {
		if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
		if (!resolved || !advanceOnEnd) return undefined;
		stallTimerRef.current = setTimeout(() => {
			setVideoFailed(true);
			if (onButtonAction) onButtonAction({ content: { action: "nextPage" } });
		}, 10000);
		return () => {
			if (stallTimerRef.current) clearTimeout(stallTimerRef.current);
		};
	}, [resolved, advanceOnEnd, onButtonAction, element.content?.url]);

	const clearStallTimer = () => {
		if (stallTimerRef.current) {
			clearTimeout(stallTimerRef.current);
			stallTimerRef.current = null;
		}
	};

	const bindVideoRate = (node) => {
		if (node) applyEscapeRoomVideoPlaybackRate(node, playbackRate);
	};
	const tryPlay = (node) => {
		if (!node) return;
		try {
			const p = node.play();
			if (p && typeof p.catch === "function") p.catch(() => {});
		} catch (_) {}
	};
	const onVideoMeta = (e) => {
		clearStallTimer();
		applyEscapeRoomVideoPlaybackRate(e.currentTarget, playbackRate);
		tryPlay(e.currentTarget);
	};
	const handleVideoEnded = advanceOnEnd && onButtonAction
		? () => {
			clearStallTimer();
			onButtonAction({ content: { action: "nextPage" } });
		}
		: undefined;
	const handleVideoError = () => {
		clearStallTimer();
		setVideoFailed(true);
		if (advanceOnEnd && onButtonAction) {
			onButtonAction({ content: { action: "nextPage" } });
		}
	};

	return (
		<div style={{ ...style, overflow: "hidden", backgroundColor: "transparent" }}>
			{resolved && !videoFailed ? (
				<video
					ref={bindVideoRate}
					src={resolved}
					controls={element.content?.showControls !== false}
					preload="metadata"
					playsInline
					loop={videoLoop}
					autoPlay={!!element.content?.autoplay || advanceOnEnd}
					muted={!!element.content?.autoplay || advanceOnEnd}
					onLoadedMetadata={onVideoMeta}
					onCanPlay={(e) => {
						clearStallTimer();
						tryPlay(e.currentTarget);
					}}
					onEnded={handleVideoEnded}
					onError={handleVideoError}
					style={{
						width: "100%",
						height: "100%",
						objectFit: "cover",
						opacity: element.content?.opacity != null ? element.content.opacity / 100 : 1,
					}}
				/>
			) : (
				<div style={{ padding: 8, color: "#94a3b8", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", height: "100%", background: "rgba(15,23,42,0.06)" }}>
					{resolved ? intl.formatMessage({ id: "QUIZ.VIDEO_UNAVAILABLE" }) : "Video"}
				</div>
			)}
		</div>
	);
}

export function EscapeRoomElement({
	element,
	quizData,
	answer,
	registrationDesign,
	onButtonAction,
	onQuizOptionSelect,
	onQuizCustomAnswer,
	onHintUsed,
	onHintDesignOpen,
	allQuizzesOnPageCompleted,
	buttonVisible,
	buttonDisabled,
	entryAnimationStyle,
	animKey = "",
	playerScore = 0,
}) {
	const intl = useIntl();
	const t = (id, values) => intl.formatMessage({ id }, values);
	const editRef = useRef(null);
	const [hintModal, setHintModal] = useState(null);
	const [quizHoveredOption, setQuizHoveredOption] = useState(-1);
	const [hintHovered, setHintHovered] = useState(false);
	const [quizPlayBreakpoint, setQuizPlayBreakpoint] = useState(() =>
		typeof window !== "undefined" ? pickErViewportFromSize(window.innerWidth, window.innerHeight) : "desktop"
	);

	useEffect(() => {
		const onResize = () => setQuizPlayBreakpoint(pickErViewportFromSize(window.innerWidth, window.innerHeight));
		if (typeof window === "undefined") return undefined;
		window.addEventListener("resize", onResize);
		return () => window.removeEventListener("resize", onResize);
	}, []);

	useEffect(() => {
		if (element.type !== "text" || !editRef.current || !element.content) return;
		const tw = resolveTextTypewriterDurationSec(element);
		if (tw > 0 && !isScoreTextElement(element.content)) return;
		const html = isScoreTextElement(element.content)
			? resolveScoreTextDisplayHtml(element.content, playerScore)
			: (element.content.html || `<p>${element.content.text || "Texto"}</p>`);
		if (editRef.current.innerHTML !== html) {
			editRef.current.innerHTML = html;
		}
	}, [element.elementId, element.type, element.content?.html, element.content?.text, element.content?.textRole, element.content?.typewriterDurationSec, element.entryAnimation, element.entryAnimationDuration, playerScore]);

	const hideAfterSec = Math.max(0, Number(element.content?.hideAfterSeconds) || 0);
	const style = {
		position: "absolute",
		left: element.x,
		top: element.y,
		width: element.width,
		height: element.height,
		overflow:
			element.type === "text" ||
			element.type === "quiz" ||
			(element.type === "button" && (buttonElementNeedsEffectOverflow(element) || hideAfterSec > 0))
				? "visible"
				: "hidden",
		boxSizing: "border-box",
		border: "none",
		outline: "none",
		zIndex: 1,
		...(entryAnimationStyle || {}),
		// Textos/imágenes/formas no capturan clics (el drop-shadow de ensamblaje agranda el hit-box)
		...(element.type === "text" || element.type === "image" || element.type === "shape"
			? { pointerEvents: "none", cursor: "default" }
			: {}),
	};

	// Text: mismo estilo que el backoffice; fontWeight del bloque se aplica al contenedor (negrita donde deba estar).
	if (element.type === "text") {
		const s = element.style || {};
		const isScore = isScoreTextElement(element.content);
		const fontFamily = s.fontFamily || "Arial, sans-serif";
		const fontSize = s.fontSize ?? 18;
		const twSec = isScore ? 0 : resolveTextTypewriterDurationSec(element);
		const typewriterOn = twSec > 0;
		const { className: textEffectClass, extraStyle: textEffectStyle } = getTextEffectPresentation(s);
		const textContentStyle = {
			padding: 8,
			fontFamily,
			fontSize: typeof fontSize === "number" ? `${fontSize}px` : fontSize,
			color: s.color || "#000",
			backgroundColor: s.backgroundColor === "transparent" ? "transparent" : (s.backgroundColor || "transparent"),
			fontWeight: s.fontWeight || "normal",
			fontStyle: s.fontStyle || "normal",
			textDecoration: s.textDecoration || "none",
			textAlign: s.textAlign || "left",
			letterSpacing: normalizeLetterSpacing(s.letterSpacing, "0px"),
			lineHeight: normalizeLineHeight(s.lineHeight, 1.3),
			width: "100%",
			height: "auto",
			minWidth: 20,
			outline: "none",
			boxSizing: "border-box",
			overflow: "visible",
			overflowX: "visible",
			overflowY: "visible",
			wordBreak: "normal",
			overflowWrap: "break-word",
			hyphens: "none",
			...textEffectStyle,
		};
		const html = isScore
			? resolveScoreTextDisplayHtml(element.content, playerScore)
			: (element.content?.html || `<p>${element.content?.text || "Texto"}</p>`);
		if (typewriterOn) {
			return (
				<div
					style={{
						...style,
						["--er-font"]: fontFamily,
						["--er-size"]: typeof fontSize === "number" ? `${fontSize}px` : fontSize,
					}}
				>
					<EscapeRoomTypewriterTextDiv
						html={html}
						durationSec={twSec}
						resetKey={animKey || element.elementId}
						className={textEffectClass}
						style={textContentStyle}
					/>
				</div>
			);
		}
		return (
			<div
				style={{
					...style,
					["--er-font"]: fontFamily,
					["--er-size"]: typeof fontSize === "number" ? `${fontSize}px` : fontSize,
				}}
			>
				<div
					ref={editRef}
					className={textEffectClass}
					style={textContentStyle}
				/>
			</div>
		);
	}

	// Image
	if (element.type === "image") {
		const isLogo = element.registrationRole === "logo" || element.registrationRole === "logoOncommit" || element.registrationRole === LEGACY_LOGO_REGISTRATION_ROLE;
		const imgUrl = isLogo && registrationDesign?.logoUrl ? registrationDesign.logoUrl : element.content?.url;
		const resolved = resolveUrl(imgUrl);
		const blendMode = element.style?.imageBlendMode || "none";
		const blendOpacityRaw = Number(element.style?.imageBlendOpacity);
		const blendOpacity = Number.isFinite(blendOpacityRaw) ? Math.max(0, Math.min(1, blendOpacityRaw)) : 0.35;
		const imageMaskId = `img-mask-${String(element.elementId || "image").replace(/[^a-zA-Z0-9_-]/g, "")}`;
		const preserveAspectRatio = isLogo ? "xMidYMid meet" : "xMidYMid slice";
		const imageTransformStyle = getImageElementTransformStyle(element.style);
		const imageMediaWrapStyle = { width: "100%", height: "100%", ...imageTransformStyle };
		return (
			<div style={style}>
				{resolved ? (
					<div
						style={{
							position: "relative",
							width: "100%",
							height: "100%",
							overflow: "hidden",
							isolation: "isolate",
							boxSizing: "border-box",
							borderRadius: element.style?.borderRadiusPercent != null ? `${element.style.borderRadiusPercent}%` : 0,
							border: resolveImageBorderCss(element.style),
						}}
					>
						{blendMode === "none" ? (
							<div style={imageMediaWrapStyle}>
								<img
									src={resolved}
									alt={element.content?.alt || ""}
									style={{
										width: "100%",
										height: "100%",
										objectFit: isLogo ? "contain" : "cover",
										opacity: element.style?.opacity ?? 1,
									}}
								/>
							</div>
						) : (
							<div style={imageMediaWrapStyle}>
								<svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ display: "block", opacity: element.style?.opacity ?? 1 }}>
								<defs>
									<mask id={imageMaskId}>
										<image href={resolved} x="0" y="0" width="100" height="100" preserveAspectRatio={preserveAspectRatio} />
									</mask>
								</defs>
								<image href={resolved} x="0" y="0" width="100" height="100" preserveAspectRatio={preserveAspectRatio} />
								<rect
									x="0"
									y="0"
									width="100"
									height="100"
									fill={element.style?.imageBlendColor || "rgba(124,58,237,1)"}
									fillOpacity={blendOpacity}
									mask={`url(#${imageMaskId})`}
									style={{ mixBlendMode: blendMode === "multiply" ? "multiply" : "normal" }}
								/>
							</svg>
							</div>
						)}
					</div>
				) : (
					<div style={{ padding: 8, color: "#999", fontSize: 12, display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>Imagen</div>
				)}
			</div>
		);
	}

	// Video
	if (element.type === "video") {
		return <EscapeRoomVideoElement element={element} style={style} onButtonAction={onButtonAction} />;
	}

	// Audio
	if (element.type === "audio") {
		return <EscapeRoomAudioElement element={element} style={style} />;
	}

	// Shape (alineado con backoffice: fillColor, strokeColor, shapeType)
	if (element.type === "shape") {
		ensureTextEffectStylesInjected();
		const shapeStyle = element.style || {};
		const shapeFx = getElementEffectPresentation(shapeStyle);
		const c = element.content || {};
		const fill = c.fillColor || c.fill || "rgba(100, 100, 255, 0.8)";
		const stroke = c.strokeColor || c.stroke || "transparent";
		const strokeW = c.strokeWidth ?? 2;
		const shapeType = c.shapeType || "square";
		const radius = c.borderRadius ?? 0;
		const opacity = element.style?.opacity ?? 1;
		const fillIsGrad = isShapeGradient(fill);
		const strokeIsGrad = isShapeGradient(stroke);
		const fillBg = fillIsGrad ? { background: fill } : { backgroundColor: fill };
		const strokeBorder = strokeIsGrad ? {} : { border: `${strokeW}px solid ${stroke}` };

		let innerStyle = {
			width: "100%",
			height: "100%",
			opacity,
			boxSizing: "border-box",
			...fillBg,
			...strokeBorder,
		};

		if (shapeType === "circle") {
			innerStyle = { ...innerStyle, borderRadius: "50%" };
		} else if (shapeType === "line") {
			innerStyle = {
				width: "100%",
				height: Math.max(strokeW, 2),
				minHeight: Math.max(strokeW, 2),
				opacity,
				...(strokeIsGrad ? { background: stroke } : { backgroundColor: stroke }),
				border: "none",
				alignSelf: "center",
				borderRadius: radius ? `${Math.min(radius, 999)}px` : 0,
			};
		} else if (shapeType === "triangle") {
			// Triángulo via SVG
			return (
				<div
					className={shapeFx.className || undefined}
					style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center", ...shapeFx.extraStyle }}
				>
					<svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ overflow: "visible", opacity }}>
						<polygon
							points="50,5 95,95 5,95"
							fill={fillIsGrad ? fill : fill}
							stroke={!strokeIsGrad && stroke !== "transparent" ? stroke : "none"}
							strokeWidth={strokeW}
						/>
					</svg>
				</div>
			);
		} else {
			innerStyle.borderRadius = radius ? `${Math.min(radius, 999)}px` : 0;
		}

		const needsStrokeWrapper = strokeIsGrad && strokeW > 0 && shapeType !== "line";
		const innerRadius = shapeType === "circle" ? "50%" : radius ? `${Math.min(radius, 999)}px` : 0;
		const wrapperStyle = needsStrokeWrapper
			? {
					width: "100%",
					height: "100%",
					padding: strokeW,
					background: stroke,
					borderRadius: shapeType === "circle" ? "50%" : radius + strokeW,
					boxSizing: "border-box",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					opacity,
			  }
			: null;
		const wrappedInnerStyle = needsStrokeWrapper
			? { flex: 1, minWidth: 0, minHeight: 0, ...fillBg, borderRadius: innerRadius, boxSizing: "border-box" }
			: null;

		return (
			<div
				className={shapeFx.className || undefined}
				style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center", ...shapeFx.extraStyle }}
			>
				{needsStrokeWrapper ? (
					<div style={wrapperStyle}>
						<div style={wrappedInnerStyle} />
					</div>
				) : (
					<div style={innerStyle} />
				)}
			</div>
		);
	}

	// Hint: ocultar cuando se han completado todos los quizzes de la página (como en backoffice)
	if (element.type === "hint") {
		if (allQuizzesOnPageCompleted) return null;
		const c = element.content || {};
		const label = c.label || t("COMMON.HINT");
		const hintText = c.hintText || "";
		const hintPenalty = c.hintPenalty ?? 0;
		const s = element.style || {};
		const hintSize = s.fontSize ?? 14;
		const borderW = Math.max(0, s.borderWidth ?? 1);
		const borderColor = s.borderColor || "rgba(124, 58, 237, 1)";
		const baseBg = s.backgroundColor || "#f5f3ff";
		const hoverBg = s.hoverBackgroundColor ?? getDefaultHoverColor(baseBg);
		const btnStyle = {
			width: "100%",
			height: "100%",
			display: "flex",
			alignItems: "center",
			justifyContent: "center",
			backgroundColor: hintHovered && hoverBg ? hoverBg : baseBg,
			color: s.color || "#7c3aed",
			fontSize: typeof hintSize === "number" ? `${hintSize}px` : hintSize,
			fontWeight: s.fontWeight || "bold",
			fontFamily: s.fontFamily || "Arial, sans-serif",
			borderRadius: (s.borderRadius ?? 8) >= 0 ? s.borderRadius : 8,
			border: borderW > 0 ? `${borderW}px solid ${borderColor}` : "none",
			cursor: "pointer",
			transition: "background-color 0.15s ease",
			...(s.letterSpacing != null && { letterSpacing: normalizeLetterSpacing(s.letterSpacing) }),
			...(s.lineHeight != null && { lineHeight: normalizeLineHeight(s.lineHeight) }),
		};
		const handleHintClick = (e) => {
			e.stopPropagation();
			const hasContent = hintText.trim() || c.hintModalDesign || c.confirmModalDesign;
			if (!hasContent) return;
			if (onHintDesignOpen && (c.confirmModalDesign || c.hintModalDesign)) {
				if (hintPenalty > 0 && (c.confirmModalDesign?.elements?.length || hintText.trim())) {
					onHintDesignOpen({
						type: "confirm",
						element,
						design: c.confirmModalDesign,
						size: c.confirmModalSize || "medium",
						penalty: hintPenalty,
						hintText,
						label,
					});
				} else if (c.hintModalDesign?.elements?.length) {
					onHintDesignOpen({
						type: "hint",
						element,
						design: c.hintModalDesign,
						size: c.hintModalSize || "medium",
						hintText,
						label,
					});
				} else {
					onHintDesignOpen({ type: "simple", hintText, label });
				}
				return;
			}
			if (hintPenalty > 0 && (c.confirmModalDesign || hintText.trim())) {
				setHintModal({ type: "confirm", penalty: hintPenalty, text: hintText, label });
			} else {
				setHintModal({ type: "hint", text: hintText, label });
			}
		};
		const handleConfirmAccept = () => {
			setHintModal({ type: "hint", text: hintModal.text });
			if (onHintUsed && hintPenalty > 0) onHintUsed(element.elementId, hintPenalty);
		};
		return (
			<>
				<div style={{ ...style, ["--er-hint-font"]: s.fontFamily || "Arial, sans-serif", ["--er-hint-size"]: typeof hintSize === "number" ? `${hintSize}px` : hintSize }} onClick={handleHintClick}>
					<div
						className="escape-room-hint-content"
						style={{ ...btnStyle, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}
						onMouseEnter={() => setHintHovered(true)}
						onMouseLeave={() => setHintHovered(false)}
					>
						<span
							className="material-icons"
							aria-hidden
							style={{
								fontSize: Number(hintSize) * 1.2,
								lineHeight: 1,
								color: "currentColor",
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								flexShrink: 0,
							}}
						>
							live_help
						</span>
						{label}
					</div>
				</div>
				{hintModal?.type === "confirm" && hintModal.penalty === hintPenalty && (
					<div className="modal" style={{ display: "flex", position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", alignItems: "center", justifyContent: "center", zIndex: 9999, animation: "er-modal-overlay-in 0.22s ease-out both" }}>
						<div style={{ background: "#fff", padding: 24, borderRadius: 8, maxWidth: 400, animation: "er-modal-content-in 0.28s cubic-bezier(0.34, 1.2, 0.64, 1) both" }}>
							<h3>{hintModal.label || t("COMMON.HINT")}</h3>
							<p>Vas a perder {hintModal.penalty} puntos. ¿Estás seguro?</p>
							<div style={{ display: "flex", gap: 12, justifyContent: "flex-end", marginTop: 16 }}>
								<button onClick={() => setHintModal(null)}>Cancelar</button>
								<button onClick={handleConfirmAccept} className="btn-main">Aceptar</button>
							</div>
						</div>
					</div>
				)}
				{hintModal?.type === "hint" && (
					<div className="modal" style={{ display: "flex", position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", alignItems: "center", justifyContent: "center", zIndex: 9999, animation: "er-modal-overlay-in 0.22s ease-out both" }}>
						<div style={{ background: "#fff", padding: 24, borderRadius: 8, maxWidth: 400, maxHeight: "80vh", overflow: "auto", animation: "er-modal-content-in 0.28s cubic-bezier(0.34, 1.2, 0.64, 1) both" }}>
							<h3>{hintModal.label || t("COMMON.HINT")}</h3>
							<div style={{ whiteSpace: "pre-wrap" }}>{hintModal.text}</div>
							<div style={{ marginTop: 16 }}>
								<button onClick={() => setHintModal(null)} className="btn-main">Cerrar</button>
							</div>
						</div>
					</div>
				)}
			</>
		);
	}

	// Button (caja fija como en backoffice: width/height del elemento, contenido centrado, hover como en backoffice)
	if (element.type === "button") {
		if (buttonVisible === false) return null;
		return (
			<ButtonWithHover
				element={element}
				style={style}
				onButtonAction={onButtonAction}
				disabled={buttonDisabled}
			/>
		);
	}

	// Quiz (alineado con backoffice: layout, tamaños, estilos)
	if (element.type === "quiz") {
		const quiz = quizData;
		const questions = quiz?.questions || [];
		const firstQ = questions[0];
		const opts = firstQ?.options || [];
		const isMatch = firstQ?.type === "match";
		const isFillBlanks = firstQ?.type === "fillBlanks";
		const isTextAnswer = firstQ?.type === "textAnswer";
		const hasQuizContent = quiz && questions.length > 0;
		const isSurveyGrading = String(quiz?.gradingMode || "").toLowerCase() === "survey";
		const isSurvey = isSurveyGrading && !isMatch && !isFillBlanks && !isTextAnswer;
		const attemptMode = quiz?.attemptMode || (quiz?.allowMultipleAttempts ? "unlimited" : "single");
		const maxAttempts = Math.max(1, quiz?.maxAttempts ?? 3);
		const totalCorrectCount = isTextAnswer
			? 1
			: isFillBlanks
			? (firstQ?.segments || []).filter((s) => s?.type === "blank").length
			: isSurvey
				? 1
				: opts.filter((o) => o?.correct).length;
		const selections = (answer?.selections || (answer?.selectedIndex != null ? [{ optionIndex: answer.selectedIndex, correct: answer.correct }] : []));
		const correctSelectedCount = selections.filter((s) => s.correct).length;
		const done = isTextAnswer
			? (answer?.done != null ? answer.done : !!answer?.confirmed)
			: isFillBlanks
			? (answer?.done != null ? answer.done : !!answer?.confirmed)
			: isMatch
				? (answer?.done != null ? answer.done : !!answer?.done)
				: isSurvey
					? selections.length >= 1
					: attemptMode === "single"
						? selections.length >= 1
						: attemptMode === "limited"
							? correctSelectedCount >= totalCorrectCount || selections.length >= maxAttempts
							: correctSelectedCount >= totalCorrectCount;
		const answered = done;
		const isImageType = firstQ?.type === "imageChoice" || firstQ?.type === "optionsWithImages";

		const qStyle = element.style || {};
		const optStyle = element.content?.quizOptionsStyle || {};
		const optionAlign = optStyle.textAlign || "left";
		const optionTextStyle = {
			fontFamily: optStyle.fontFamily || "Arial, sans-serif",
			fontSize: optStyle.fontSize || 12,
			color: optStyle.color || "#334155",
			fontWeight: optStyle.fontWeight || "normal",
			fontStyle: optStyle.fontStyle || "normal",
			textDecoration: optStyle.textDecoration || "none",
			textAlign: optionAlign,
		};
		const defaultOptionTextEffect = getTextEffectPresentation(
			{ ...optStyle, color: optStyle.color || "#334155" },
			{ baseClass: "" },
		);
		const quizOptionsCustomBox = element.content?.quizOptionsCustom || {};
		const quizBoxDesignId = element.content?.quizOptionsDesign || "classic";
		const quizBoxDesignDefaults = QUIZ_OPTION_DESIGNS[quizBoxDesignId] || QUIZ_OPTION_DESIGNS.classic;
		const optionBoxFxColor = quizOptionsCustomBox.optionBorderColor || quizBoxDesignDefaults.defaultBorder || optStyle.color || "#334155";
		const optionBoxFx = getElementEffectPresentation(
			{ ...pickEffectBundle(quizOptionsCustomBox, "optionBox"), color: optionBoxFxColor },
			{ preferBoxShadow: true },
		);
		const optionBoxGlitchActive = hasActiveEffectBundle(quizOptionsCustomBox, "optionBox")
			&& pickEffectBundle(quizOptionsCustomBox, "optionBox").textEffect === "glitch";
		const textAnswerFieldPresentation = isTextAnswer
			? buildTextAnswerFieldPresentation({
				element,
				optionTextStyle,
				optionBoxFx,
				state: {
					done,
					isSelected: !!answer?.confirmed,
					correct: isSurveyGrading ? true : !!answer?.correct,
					sel: answer?.confirmed ? answer : null,
				},
				context: { index: 0, totalCount: 1, questionType: firstQ?.type, surveyMode: isSurveyGrading },
			})
			: null;

		if (!hasQuizContent) {
			return (
				<div style={{ ...style, display: "flex", alignItems: "center", justifyContent: "center", background: "#f8fafc", borderRadius: 8 }}>
					<span style={{ color: "#94a3b8", fontSize: 12 }}>Quiz sin configurar</span>
				</div>
			);
		}

		const layout = getQuizOptionsLayoutForBreakpoint(firstQ, quizPlayBreakpoint);
		/** En juego: permitir varias líneas en opciones (evita texto invisible por ellipsis o blanco sobre blanco). */
		const quizOptionLabelWraps =
			layout === "columns" || layout === "grid" || quizPlayBreakpoint !== "desktop";
		const gridCols = Math.min(6, Math.max(1, firstQ?.gridColumns || 2));
		const canClickOption = (i) => !done && !selections.some((s) => s.optionIndex === i);
		const handleOptionClick = onQuizOptionSelect ? (i) => { if (canClickOption(i)) onQuizOptionSelect(element.elementId, i, isSurvey ? true : !!opts[i]?.correct); } : undefined;
		const qcMarker = element?.content?.quizOptionsCustom || {};
		const markerMs = String(qcMarker.markerShape ?? "").trim().toLowerCase();
		const markerHasExplicitShape = String(qcMarker.markerShape ?? "").trim() !== "";
		const markerIsExplicitRound = markerHasExplicitShape && (markerMs === "round" || markerMs === "circle" || markerMs === "redonda");
		const useSquareMarker = markerHasExplicitShape && !markerIsExplicitRound;
		const markerCornerPxGlob = Number.isFinite(Number(qcMarker.markerRadius)) ? Math.max(0, Math.min(12, Number(qcMarker.markerRadius))) : (markerIsExplicitRound ? 4 : 2);
		const markerCornerCssGlob = `${markerCornerPxGlob}px`;

		let containerStyle = {};
		if (isImageType) {
			if (layout === "columns") {
				containerStyle = { flex: 1, display: "flex", flexDirection: "row", flexWrap: "nowrap", gap: 6, minHeight: 0, minWidth: 0, overflow: "hidden", height: "100%" };
			} else if (layout === "row") {
				containerStyle = { flex: 1, display: "flex", flexDirection: "column", flexWrap: "nowrap", gap: 6, minHeight: 0, minWidth: 0, overflow: "hidden", height: "100%" };
			} else {
				containerStyle = { flex: 1, display: "grid", gap: 6, minHeight: 0, minWidth: 0, overflow: "hidden", height: "100%", gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`, gridAutoRows: "minmax(0, 1fr)" };
			}
		} else {
			const designId = element?.content?.quizOptionsDesign || "classic";
			const design = QUIZ_OPTION_DESIGNS[designId] || QUIZ_OPTION_DESIGNS.classic;
			const isSegmented = design.layout === "segmented";
			const isBigButtons = design.layout === "bigButtons";
			const qcBorder = element?.content?.quizOptionsCustom || {};
			const optBorderW = qcBorder.optionBorderWidth != null && Number.isFinite(Number(qcBorder.optionBorderWidth))
				? Math.max(0, Number(qcBorder.optionBorderWidth))
				: (design.borderWidth ?? 1);
			const borderInset = optBorderW > 0 ? Math.min(4, optBorderW) : 0;
			containerStyle = {
				flex: 1,
				display: layout === "grid" ? "grid" : "flex",
				flexDirection: layout === "columns" ? "row" : "column",
				flexWrap: "nowrap",
				gridTemplateColumns: layout === "grid" ? `repeat(${gridCols}, minmax(0, 1fr))` : undefined,
				gap: isSegmented ? (optBorderW > 0 ? Math.max(4, optBorderW) : 0) : (isBigButtons ? 12 : Math.max(6, optBorderW > 0 ? optBorderW + 2 : 0)),
				minHeight: 0,
				minWidth: 0,
				overflow: "visible",
				padding: borderInset > 0 ? borderInset : undefined,
				boxSizing: "border-box",
			};
		}

		const qSize = qStyle.fontSize ?? 14;
		const optSize = optStyle.fontSize ?? 12;
		const quizVars = {
			["--er-q-font"]: (qStyle.fontFamily || "Arial, sans-serif"),
			["--er-q-size"]: typeof qSize === "number" ? `${qSize}px` : String(qSize || "14px"),
			["--er-opt-font"]: (optStyle.fontFamily || "Arial, sans-serif"),
			["--er-opt-size"]: typeof optSize === "number" ? `${optSize}px` : String(optSize || "12px"),
		};
		const tfColors = getQuizTrueFalseColors(element);
		return (
			<div
				data-er-match-host={isMatch ? "1" : undefined}
				style={{ ...style, ...quizVars, display: "flex", flexDirection: "column", overflow: "hidden", background: getQuizBlockBackground(element), borderRadius: 8, boxShadow: getQuizBlockBoxShadow(element), border: getQuizBlockBorderCss(element) }}
			>
				<div style={{ flex: 1, overflow: "hidden", padding: isImageType ? 6 : isMatch ? 3 : isTextAnswer ? 6 : 10, display: "flex", flexDirection: "column", gap: isImageType ? 0 : isMatch ? 0 : isTextAnswer ? 0 : 8, minHeight: 0, boxSizing: "border-box" }}>
					{isMatch ? (
						<MatchQuizPlay
							firstQ={firstQ}
							done={done}
							answer={answer}
							optionTextStyle={optionTextStyle}
							onConfirm={onQuizCustomAnswer ? (data) => onQuizCustomAnswer(element.elementId, "match", data) : undefined}
						/>
					) : isFillBlanks ? (
						<FillBlanksQuizPlay
							firstQ={firstQ}
							done={done}
							answer={answer}
							optionTextStyle={optionTextStyle}
							onConfirm={onQuizCustomAnswer ? (data) => onQuizCustomAnswer(element.elementId, "fillBlanks", data) : undefined}
						/>
					) : isTextAnswer ? (
						<div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", overflow: "hidden", width: "100%" }}>
							<TextAnswerQuizPlay
								firstQ={firstQ}
								done={done}
								answer={answer}
								optionTextStyle={optionTextStyle}
								fieldContainerStyle={textAnswerFieldPresentation?.containerStyle}
								fieldInputStyle={textAnswerFieldPresentation?.inputStyle}
								fieldClassName={textAnswerFieldPresentation?.className}
								fieldSubmitStyle={textAnswerFieldPresentation?.submitButtonStyle}
								fieldSubmitClassName={textAnswerFieldPresentation?.submitButtonClassName}
								onConfirm={onQuizCustomAnswer ? (data) => onQuizCustomAnswer(element.elementId, "textAnswer", data) : undefined}
							/>
						</div>
					) : (
					<div style={containerStyle}>
						{opts.map((opt, i) => {
							const sel = selections.find((s) => s.optionIndex === i);
							const isSelected = !!sel;
							const canHover = !!handleOptionClick && canClickOption(i);
							const optionHovered = quizHoveredOption === i && canHover;
							const optionLabel = (firstQ.type === "imageChoice" && !opt.text) ? t("COMMON.OPTION_N", { n: i + 1 }) : (opt.text || t("COMMON.OPTION"));
							const hasImg = opt.imageUrl && String(opt.imageUrl).trim();
							const optStyles = !isImageType
								? getQuizOptionStyles(
									element,
									{ done, isSelected, correct: opt.correct, sel },
									{ index: i, totalCount: opts.length, questionType: firstQ?.type, surveyMode: isSurvey }
								)
								: null;
							if (isImageType) {
								// Colores V/F solo para trueFalse y solo si están definidos; si no, usar normales
								const useTfForImage = firstQ?.type === "trueFalse" && (element?.content?.quizTrueColor || element?.content?.quizFalseColor);
								const imgTrueColor = useTfForImage ? tfColors.trueColor : "#22c55e";
								const imgFalseColor = useTfForImage ? tfColors.falseColor : "#ef4444";
								const showOptionFeedbackColors = !isSurvey && (done || isSelected);
								const cardBg = "transparent";
								const borderColor = isSurvey
									? (isSelected ? "#6366f1" : "#e2e8f0")
									: showOptionFeedbackColors
										? (opt.correct ? imgTrueColor : (isSelected && !sel?.correct ? imgFalseColor : "#e2e8f0"))
										: "#e2e8f0";
								const cardHoverBorder = getDefaultHoverColor(borderColor) || borderColor;
								const finalBg = cardBg;
								const finalBorder = optionHovered ? cardHoverBorder : borderColor;
								const imageCardBase = {
									display: "flex",
									flexDirection: "column",
									position: "relative",
									borderRadius: 8,
									background: finalBg,
									border: "2px solid",
									borderColor: finalBorder,
									overflow: optionBoxGlitchActive ? "visible" : "hidden",
									cursor: handleOptionClick ? "pointer" : "default",
									touchAction: "manipulation",
									boxSizing: "border-box",
									flex: "1 1 0",
									minWidth: 0,
									minHeight: 0,
									transition: "border-color 0.2s ease, box-shadow 0.2s ease",
									boxShadow: optionHovered ? "0 4px 12px rgba(0,0,0,0.12)" : "0 1px 4px rgba(0,0,0,0.06)",
								};
								const { boxShadow: imageBoxFxShadow, filter: imageBoxFxFilter, ...imageBoxFxRest } = optionBoxFx.extraStyle || {};
								const imageCardStyle = {
									...imageCardBase,
									...imageBoxFxRest,
									...(imageBoxFxFilter ? { filter: imageBoxFxFilter } : {}),
									boxShadow: mergeBoxShadow(imageCardBase.boxShadow, imageBoxFxShadow),
								};
								return (
									<div
										key={i}
										className={optionBoxFx.className || undefined}
										role="button"
										onClick={handleOptionClick ? () => handleOptionClick(i) : undefined}
										onPointerUp={handleOptionClick ? (ev) => { ev.preventDefault(); handleOptionClick(i); } : undefined}
										onMouseEnter={() => canHover && setQuizHoveredOption(i)}
										onMouseMove={() => canHover && setQuizHoveredOption(i)}
										onMouseLeave={() => setQuizHoveredOption(-1)}
										onPointerEnter={() => canHover && setQuizHoveredOption(i)}
										onPointerLeave={() => setQuizHoveredOption(-1)}
										style={imageCardStyle}
									>
										<div style={{ position: "relative", flex: 1, minHeight: 0, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
											{hasImg ? (
												<img src={resolveUrl(opt.imageUrl)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
											) : (
												<span style={{ fontSize: 32, color: "#94a3b8" }}>🖼</span>
											)}
											{(done || isSelected) && !isSurvey && (
												<div style={{ position: "absolute", top: 4, right: 4 }}>
													{opt.correct && <span style={{ color: imgTrueColor, background: "rgba(255,255,255,0.9)", borderRadius: 4, padding: 2 }}>✓</span>}
													{isSelected && !sel?.correct && <span style={{ color: imgFalseColor, background: "rgba(255,255,255,0.9)", borderRadius: 4, padding: 2 }}>✗</span>}
												</div>
											)}
											{isSurvey && isSelected && done && (
												<div style={{ position: "absolute", top: 4, right: 4 }}>
													<span style={{ color: "#4f46e5", background: "rgba(255,255,255,0.9)", borderRadius: 4, padding: 2 }}>✓</span>
												</div>
											)}
										</div>
										{firstQ.type === "optionsWithImages" && (
											<div
												className="escape-room-quiz-option"
												style={{
													padding: "4px 6px",
													...optionTextStyle,
													textAlign: "center",
													fontSize: (optionTextStyle.fontSize || 12) * 0.95,
													flexShrink: 0,
													background: "transparent",
												}}
											>
												<span
													className={defaultOptionTextEffect.className || undefined}
													style={{
														...(quizOptionLabelWraps
															? { display: "block", whiteSpace: "normal", wordBreak: "break-word", overflowWrap: "break-word", lineHeight: 1.35, textAlign: "center" }
															: { overflow: "hidden", textOverflow: "ellipsis", display: "block", whiteSpace: "nowrap" }),
														...defaultOptionTextEffect.extraStyle,
													}}
												>
													{optionLabel}
												</span>
											</div>
										)}
									</div>
								);
							}

							const hasCustomOptionBg = !!(qcMarker.optionBg && String(qcMarker.optionBg).trim());
							const optionColor = hasCustomOptionBg && (done || isSelected)
								? (optionTextStyle.color || optStyles.optionColor || "#f8fafc")
								: (optStyles.optionColor || optionTextStyle.color);
							const isSegmented = optStyles.layout === "segmented";
							const isMinimal = optStyles.layout === "minimal";
							const isBigButtons = optStyles.layout === "bigButtons";
							const isCardChoice = optStyles.layout === "cardChoice";
							const showTfColors = !isSurvey && (done || isSelected);
							const showOptionFeedback = showTfColors && (opt.correct || (isSelected && sel && !sel.correct));
							const letterCircleBg = isSurvey && isSelected
								? "#c7d2fe"
								: showOptionFeedback
									? (opt.correct ? optStyles.letterCircleCorrectBg : optStyles.letterCircleIncorrectBg)
									: optStyles.letterCircleDefaultBg;
							const letterCircleContent = isSurvey && isSelected
								? "✓"
								: showOptionFeedback
									? (opt.correct ? "✓" : "✗")
									: String.fromCharCode(65 + (i % 26));
							const isHovered = optionHovered;
							const useTfForHover = !isSurvey && firstQ?.type === "trueFalse" && (element?.content?.quizTrueColor || element?.content?.quizFalseColor);
							const hoverBaseColor = useTfForHover
								? (opt.correct ? tfColors.trueColor : tfColors.falseColor)
								: optStyles.backgroundColor;
							const hasCustomHoverBg = !!optStyles.hoverBackgroundColor;
							const autoHoverBgRaw = getDefaultHoverColor(hoverBaseColor);
							const autoHoverBg = (autoHoverBgRaw && autoHoverBgRaw !== "transparent") ? autoHoverBgRaw : hoverBaseColor;
							const resolvedHoverBg = optStyles.hoverBackgroundColor || autoHoverBg;
							const optionBg = isHovered && canHover ? resolvedHoverBg : optStyles.backgroundColor;
							const optionBorderCol = isHovered && canHover && !hasCustomHoverBg
								? (autoHoverBgRaw || autoHoverBg)
								: optStyles.borderColor;
							const hoverOuterShadow = isHovered && canHover ? "0 4px 12px rgba(0,0,0,0.12)" : undefined;
							const optionBorderStyle = resolveQuizOptionBorderPresentation(
								optStyles.borderWidth,
								optionBorderCol,
								mergeBoxShadow(optStyles.boxShadow, hoverOuterShadow),
							);
							const resolvedOptionColor = isHovered && canHover && optStyles.hoverOptionColor
								? optStyles.hoverOptionColor
								: optionColor;
							const { className: optionTextEffectClass, extraStyle: optionTextEffectStyle } = getTextEffectPresentation(
								{ ...optStyle, color: resolvedOptionColor },
								{ baseClass: "" },
							);
							const listOptionBase = {
								display: "flex",
								alignItems: "center",
								justifyContent: isBigButtons ? "center" : undefined,
								gap: optStyles.layout === "letter" ? 14 : optStyles.layout === "radio" ? 14 : (isBigButtons ? 10 : 6),
								padding: optStyles.padding,
								borderRadius: optStyles.borderRadius,
								...optionTextStyle,
								color: resolvedOptionColor,
								cursor: handleOptionClick ? "pointer" : "default",
								touchAction: "manipulation",
								minHeight: isBigButtons ? 52 : 28,
								flex: "1 1 auto",
								minWidth: 0,
								boxSizing: "border-box",
								fontWeight: isBigButtons ? "600" : optionTextStyle.fontWeight,
								fontSize: isBigButtons ? (optionTextStyle.fontSize || 12) + 2 : optionTextStyle.fontSize,
								transition: "background 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease",
								background: optionBg,
								backgroundColor: optionBg,
								...optionBorderStyle,
							};
							const { boxShadow: listBoxFxShadow, filter: listBoxFxFilter, ...listBoxFxRest } = optionBoxFx.extraStyle || {};
							const listOptionStyle = {
								...listOptionBase,
								...listBoxFxRest,
								...(listBoxFxFilter ? { filter: listBoxFxFilter } : {}),
								boxShadow: mergeBoxShadow(listOptionBase.boxShadow, listBoxFxShadow),
							};
							const renderPlayMarkerLeft = () => {
								if (useSquareMarker) {
									const w = optStyles.markerCustom?.enabled ? optStyles.markerCustom.size : 18;
									const h = w;
									const borderCol = isSurvey && isSelected ? "#818cf8" : isSelected ? (opt.correct ? "#22c55e" : "#ef4444") : (optStyles.markerCustom?.borderColor || "#94a3b8");
									const bgFill = isSurvey && isSelected ? "rgba(99,102,241,0.15)" : isSelected ? (opt.correct ? "rgba(34,197,94,0.22)" : "rgba(239,68,68,0.18)") : (optStyles.markerCustom?.fill || "transparent");
									const markColor = isSurvey && isSelected ? "#4f46e5" : isSelected ? (opt.correct ? "#16a34a" : "#dc2626") : "transparent";
									const showCrossInBox = !isSurvey && isSelected && !sel?.correct;
									return (
										<span style={{ width: w, height: h, border: `2px solid ${borderCol}`, borderRadius: markerCornerCssGlob, boxSizing: "border-box", display: "inline-flex", alignItems: "center", justifyContent: "center", background: bgFill, flexShrink: 0 }} aria-hidden>
											{isSelected ? (
												<span style={{ fontSize: showCrossInBox ? 12 : 11, fontWeight: 700, color: markColor, lineHeight: 1 }}>
													{showCrossInBox ? "✗" : "✓"}
												</span>
											) : null}
										</span>
									);
								}
								if (isSelected) {
									if (isSurvey) {
										return <span style={{ fontSize: 15, fontWeight: 700, color: "#4f46e5", lineHeight: 1 }}>✓</span>;
									}
									if (!opt.correct) {
										return <span style={{ fontSize: 15, fontWeight: 700, color: "#dc2626", lineHeight: 1 }}>✗</span>;
									}
									return <span style={{ fontSize: 15, fontWeight: 700, color: "#16a34a", lineHeight: 1 }}>✓</span>;
								}
								return <span style={{ width: 18, height: 18, border: `2px solid ${optStyles.markerCustom?.borderColor || "#94a3b8"}`, borderRadius: markerCornerCssGlob, boxSizing: "border-box", display: "inline-block", flexShrink: 0, background: optStyles.markerCustom?.fill || "transparent" }} aria-hidden />;
							};
							return (
								<div
									key={i}
									className={["escape-room-quiz-option", optionBoxFx.className].filter(Boolean).join(" ") || undefined}
									role="button"
									onClick={handleOptionClick ? () => handleOptionClick(i) : undefined}
									onPointerUp={handleOptionClick ? (ev) => { ev.preventDefault(); handleOptionClick(i); } : undefined}
									onMouseEnter={() => canHover && setQuizHoveredOption(i)}
									onMouseMove={() => canHover && setQuizHoveredOption(i)}
									onMouseLeave={() => setQuizHoveredOption(-1)}
									onPointerEnter={() => canHover && setQuizHoveredOption(i)}
									onPointerLeave={() => setQuizHoveredOption(-1)}
									style={listOptionStyle}
								>
									{optStyles.layout === "letter" && (
										<span
											style={{
												width: 40,
												height: 40,
												borderRadius: "50%",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												fontSize: (done || isSelected) ? 18 : 16,
												fontWeight: 700,
												background: letterCircleBg,
												color: isSurvey && isSelected ? "#312e81" : (showTfColors ? "#fff" : (optStyles.letterCircleDefaultColor || "#475569")),
												flexShrink: 0,
											}}
										>
											{letterCircleContent}
										</span>
									)}
									{optStyles.layout === "radio" && (
										<span
											style={{
												width: optStyles.radioSize || 26,
												height: optStyles.radioSize || 26,
												borderRadius: "50%",
												display: "flex",
												alignItems: "center",
												justifyContent: "center",
												fontSize: 14,
												fontWeight: 700,
												color: "#fff",
												background: isSurvey && isSelected ? "#6366f1" : (showOptionFeedback ? (opt.correct ? (optStyles.trueColor || "#22c55e") : (optStyles.falseColor || "#ef4444")) : "transparent"),
												border: `2px solid ${isSurvey && isSelected ? "#4f46e5" : (showOptionFeedback ? (opt.correct ? (optStyles.trueColor || "#22c55e") : (optStyles.falseColor || "#ef4444")) : "#94a3b8")}`,
												flexShrink: 0,
											}}
										>
											{isSurvey ? ((done || isSelected) && isSelected ? "✓" : "") : (showOptionFeedback ? (opt.correct ? "✓" : "✗") : "")}
										</span>
									)}
									{optStyles.layout !== "letter" && optStyles.layout !== "radio" && optStyles.iconLeft ? (
										<span style={{ width: optStyles.markerCustom?.enabled ? optStyles.markerCustom.size : 20, flexShrink: 0, textAlign: "center", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
											{renderPlayMarkerLeft()}
										</span>
									) : null}
									<span
										className={optionTextEffectClass || undefined}
										style={{
											flex: isBigButtons ? "0 1 auto" : 1,
											minWidth: 0,
											textAlign: optionTextStyle.textAlign,
											lineHeight: 1.35,
											color: resolvedOptionColor,
											transition: "color 0.2s ease",
											...(quizOptionLabelWraps || isBigButtons
												? {
													overflow: "visible",
													whiteSpace: "normal",
													wordBreak: "break-word",
													overflowWrap: "break-word",
												}
												: {
													overflow: "hidden",
													textOverflow: "ellipsis",
													whiteSpace: "nowrap",
												}),
											...optionTextEffectStyle,
										}}
									>
										{optionLabel}
									</span>
									{optStyles.iconRight && (
										<span style={{ width: 16, flexShrink: 0, textAlign: "right", fontSize: 14, display: "flex", alignItems: "center", justifyContent: "center" }}>
											{!done && !optStyles.iconLeft ? (
												useSquareMarker ? (
													<span style={{ width: 16, height: 16, border: `2px solid ${optStyles.markerCustom?.borderColor || "#94a3b8"}`, borderRadius: markerCornerCssGlob, boxSizing: "border-box", display: "inline-block", flexShrink: 0 }} />
												) : null
											) : done ? (
												<>
													{isSurvey && isSelected ? (
														<span style={{ color: "#6366f1", fontWeight: 800 }}>✓</span>
													) : !isSurvey ? (
														<>
															{opt.correct && <span style={{ color: optStyles.trueColor || "#22c55e" }}>✓</span>}
															{isSelected && !sel?.correct && <span style={{ color: optStyles.falseColor || "#ef4444" }}>✗</span>}
														</>
													) : null}
												</>
											) : isSurvey && isSelected ? (
												<span style={{ color: "#6366f1", fontWeight: 800 }}>✓</span>
											) : null}
										</span>
									)}
								</div>
							);
						})}
					</div>
					)}
									{answered && firstQ.explanation && (
						<div style={{ fontSize: 11, color: "#64748b", fontStyle: "italic", marginTop: 4, padding: 6, background: "#f8fafc", borderRadius: 6, flexShrink: 0 }}>💡 {firstQ.explanation}</div>
					)}
				</div>
			</div>
		);
	}

	return null;
}
